if (!customElements.get('wls-multi-variant-picker')) {
  class MultiVariantPicker extends HTMLElement {
    constructor() {
      super();

      /** @type {Map<number, number>} variantId → quantity */
      this.quantities = new Map();
      this.cart = null;
      this.multiOptionIndex = Number(this.dataset.multiOptionPosition) - 1;

      this.boundHandleClick = this._handleClick.bind(this);
      this.boundHandlePointerUp = this._handlePointerUp.bind(this);
      this.boundHandleSubmit = this._handleSubmit.bind(this);
      this.boundHandleFilterChange = this._handleFilterChange.bind(this);
    }

    get elements() {
      return {
        variantRows: this.querySelectorAll('[data-variant-id]'),
        filtersContainer: this.querySelector('.wls-multi-variant-picker__standard-options'),
        submitButton: this.querySelector('.wls-multi-variant-picker__submit'),
        submitText: this.querySelector('.wls-multi-variant-picker__submit-text'),
        spinner: this.querySelector('.loading__spinner'),
        errorWrapper: this.querySelector('.wls-multi-variant-picker__error'),
        errorMessage: this.querySelector('.wls-multi-variant-picker__error .product-form__error-message'),
      };
    }

    connectedCallback() {
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      this.addEventListener('click', this.boundHandleClick);
      this.addEventListener('pointerup', this.boundHandlePointerUp);
      this.elements.submitButton?.addEventListener('click', this.boundHandleSubmit);
      this.elements.filtersContainer?.addEventListener('change', this.boundHandleFilterChange);

      this._syncVisibility();
      this._updateSubmitButton();
      this._updateLoadMoreVisibility();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundHandleClick);
      this.removeEventListener('pointerup', this.boundHandlePointerUp);
      this.elements.submitButton?.removeEventListener('click', this.boundHandleSubmit);
      this.elements.filtersContainer?.removeEventListener('change', this.boundHandleFilterChange);
    }

    _handleClick(event) {
      const loadMoreButton = event.target.closest('[data-load-more]');
      if (loadMoreButton && this.contains(loadMoreButton) && !loadMoreButton.disabled) {
        const isExpanded = this.classList.toggle('is-expanded');
        loadMoreButton.setAttribute('aria-expanded', String(isExpanded));
        this._updateScrollMessageText(loadMoreButton);
        return;
      }

      if (event.detail === 0) {
        this._handleQuantityAction(event.target);
      }
    }

    _handlePointerUp(event) {
      if (!event.isPrimary || event.button !== 0) return;
      this._handleQuantityAction(event.target);
    }

    _handleQuantityAction(target) {
      const button = target.closest('[data-quantity-action]');
      if (!button || !this.contains(button)) return;

      const row = button.closest('[data-variant-id]');
      if (!row || row.hidden) return;

      const variantId = Number(row.dataset.variantId);
      const available = row.dataset.variantAvailable === 'true';
      if (!available) return;

      const currentQuantity = this.quantities.get(variantId) || 0;
      const nextQuantity =
        button.dataset.quantityAction === 'increment' ? currentQuantity + 1 : Math.max(0, currentQuantity - 1);

      if (nextQuantity > 0) {
        this.quantities.set(variantId, nextQuantity);
      } else {
        this.quantities.delete(variantId);
      }

      this._renderQuantity(row, nextQuantity);
      this._updateSubmitButton();
    }

    _handleFilterChange(event) {
      const radio = event.target.closest('input[type="radio"]');
      if (radio) {
        const grid = radio.closest('.variant-option-grid');
        if (grid) {
          grid.querySelectorAll('.variant-option-grid-item').forEach((item) => {
            item.classList.remove('option-selected');
          });
          radio.closest('.variant-option-grid-item')?.classList.add('option-selected');
        }
      }

      window.requestAnimationFrame(() => {
        this._syncVisibility();
        this._updateLoadMoreVisibility();
      });
    }

    _getSelectedFilters() {
      const filters = {};
      const inputs = this.elements.filtersContainer?.querySelectorAll(
        'input[type="radio"]:checked, input[type="hidden"]'
      );

      inputs?.forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        if (position >= 0) {
          filters[position] = input.value;
        }
      });

      return filters;
    }

    _syncVisibility() {
      const filters = this._getSelectedFilters();
      const mobileVisibleCount = Number(this.dataset.mobileVisibleOptionCount) || 4;
      let visibleIndex = 0;

      this.elements.variantRows.forEach((row) => {
        const options = JSON.parse(row.dataset.variantOptions);
        const matches = Object.entries(filters).every(([position, value]) => {
          if (Number(position) === this.multiOptionIndex) return true;
          return options[Number(position)] === value;
        });

        row.hidden = !matches;
        row.classList.remove('is-mobile-hidden');

        if (matches) {
          const available = row.dataset.variantAvailable === 'true';
          row.classList.toggle('is-unavailable', !available);

          if (visibleIndex >= mobileVisibleCount) {
            row.classList.add('is-mobile-hidden');
          }
          visibleIndex++;

          const variantId = Number(row.dataset.variantId);
          const quantity = this.quantities.get(variantId) || 0;
          this._renderQuantity(row, quantity);
        }
      });
    }

    _renderQuantity(row, quantity) {
      const quantityValue = row.querySelector('.wls-multi-variant-picker__quantity-value');
      const decrementButton = row.querySelector('[data-quantity-action="decrement"]');

      if (quantityValue) quantityValue.textContent = quantity;
      if (decrementButton) decrementButton.disabled = quantity === 0;
      row.classList.toggle('is-selected', quantity > 0);
    }

    _getCartItems() {
      return Array.from(this.quantities, ([variantId, quantity]) => ({ id: variantId, quantity }));
    }

    _updateSubmitButton() {
      const button = this.elements.submitButton;
      if (!button) return;

      const items = this._getCartItems();
      const itemCount = items.reduce((total, item) => total + item.quantity, 0);
      const totalPrice = items.reduce((total, item) => {
        const row = this.querySelector(`[data-variant-id="${item.id}"]`);
        const price = row ? Number(row.dataset.variantPrice) : 0;
        return total + price * item.quantity;
      }, 0);
      const addText = button.dataset.addText?.trim() || 'Add to Cart';
      const populatedText = /^add to cart$/i.test(addText)
        ? `Add ${itemCount} to Cart`
        : `${addText} ${itemCount}`;

      button.disabled = itemCount === 0;
      this.elements.submitText.textContent =
        itemCount === 0
          ? button.dataset.emptyText
          : `${populatedText} (${this._formatMoney(totalPrice)})`;
    }

    _formatMoney(cents) {
      if (window.WLSHelpers?.formatMoney) {
        return window.WLSHelpers.formatMoney(cents, this.dataset.moneyFormat);
      }

      return `$${(cents / 100).toFixed(2)}`;
    }

    async _handleSubmit() {
      const items = this._getCartItems();
      if (!items.length || this.elements.submitButton?.getAttribute('aria-disabled') === 'true') return;

      this._setLoading(true);
      this._showError();

      try {
        const body = { items };

        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section?.section || section.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement?.(document.activeElement);
        }

        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || window.cartStrings.error);
        }

        if (this.cart) {
          data.product_title = this.dataset.productTitle;
          this.cart.renderContents(data);
        } else {
          window.location.href = window.routes.cart_url;
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'wls-multi-variant-picker',
          productVariantId: items[0].id,
          cartData: data,
        });

        this._resetQuantities();
      } catch (error) {
        console.error(error);
        this._showError(error.message);
      } finally {
        this._setLoading(false);
      }
    }

    _setLoading(isLoading) {
      const button = this.elements.submitButton;
      if (!button) return;

      button.setAttribute('aria-disabled', String(isLoading));
      button.classList.toggle('loading', isLoading);
      this.elements.spinner?.classList.toggle('hidden', !isLoading);
      this.elements.submitText?.classList.toggle('hidden', isLoading);
    }

    _showError(message = '') {
      if (!this.elements.errorWrapper || !this.elements.errorMessage) return;

      this.elements.errorWrapper.hidden = !message;
      this.elements.errorMessage.textContent = message;
    }

    _updateScrollMessageText(button) {
      const textEl = button.querySelector('.js-wls-scroll-message-text');
      if (!textEl) return;

      const isExpanded = this.classList.contains('is-expanded');
      textEl.textContent = isExpanded
        ? button.dataset.collapseText
        : (button.dataset.mobileExpandText || button.dataset.expandText);
    }

    _updateLoadMoreVisibility() {
      const loadMoreButton = this.querySelector('[data-load-more]');
      if (!loadMoreButton) return;

      const mobileVisibleCount = Number(this.dataset.mobileVisibleOptionCount) || 4;
      const visibleRows = Array.from(this.elements.variantRows).filter((row) => !row.hidden);
      const needsLoadMore = visibleRows.length > mobileVisibleCount;

      loadMoreButton.disabled = !needsLoadMore;

      const remaining = visibleRows.length - mobileVisibleCount;
      const mobileExpandText = needsLoadMore
        ? `Tap to load ${remaining} more ${remaining === 1 ? 'scent' : 'scents'}`
        : `All ${visibleRows.length} ${visibleRows.length === 1 ? 'scent' : 'scents'} shown`;
      loadMoreButton.dataset.mobileExpandText = mobileExpandText;

      this.classList.remove('is-expanded');
      loadMoreButton.setAttribute('aria-expanded', 'false');
      this._updateScrollMessageText(loadMoreButton);
    }

    _resetQuantities() {
      this.quantities.clear();
      this.elements.variantRows.forEach((row) => this._renderQuantity(row, 0));
      this._updateSubmitButton();
    }
  }

  customElements.define('wls-multi-variant-picker', MultiVariantPicker);
}
